import React, { useMemo, useCallback, useContext, useEffect, useRef } from 'react';
import { DEFAULT_AVATAR, getRingClass } from '../../../../../shared/utils/matrix/matrixRoomUtils';
import { useNavigation } from '../../../../../shared/utils/useNavigation';
import LazyImage from '../../../../../components/LazyImage/LazyImage';
import { extendTipDeadline } from '../../../../../shared/actions/tipAction';
import { useDispatch } from 'react-redux';
import { fetchMatrixRooms } from '../../../../../shared/actions/matrixAction';
import {
  WHALE_THRESHOLD,
  PREVIEW_CHAR_LIMIT,
  MEDIA_ICON_MAP,
  MESSAGE_REPLY_DEADLINE_DAYS_NOTIFY,
  MESSAGE_DEADLINE_DAYS_EXTENDED_NOTIFY,
} from '../../../../../shared/constants/message';
import { getPreviewData, isMongoObjectIdString } from '../../../../../shared/utils/chatUtil';
import { MatrixContext } from '../../../../../MatrixProvider';
import { updateMatrixEventJsonBodyKey } from '../../../../../shared/utils/matrix/matrixReadUtils';
import { captureChatException } from 'shared/sentry';
import { formatAmountWithTwoDecimalPlaces } from '../../../../../shared/utils/function';
import { getLocalItem } from '../../../../../shared/utils/config/localStorage';

const truncate = (text, limit) =>
  text.length > limit ? `${text.slice(0, limit)}…` : text;

const MessageCard = ({ item, fromTab = null, isCreator = false, refreshRooms = null }) => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const { client } = useContext(MatrixContext);

  const {
    roomId: rId,
    _id,
    username,
    avatar,
    profilePicture,
    message,
    lastMessage,
    last_message,
    time = '',
    msg_count = 0,
    badge,
    daysRemaining,
    extended,
    userId,
    isPriority,
    daysLeftToReply,
    total,
    tipId,
    matrixEventId,
    isExtended,
    pendingTips,
    isFirstMessageTip,
    isCreatorReplyToTip,
    lastMessageSenderId,
  } = item || {};
  const roomId = rId ?? _id;
  const displayName = username ?? 'Unknown';
  const avatarSrc = avatar ?? profilePicture ?? DEFAULT_AVATAR;
  const canOpenChat = isMongoObjectIdString(userId != null ? String(userId) : '');

  const currentMatrixUserId = getLocalItem('matrixUserId', null);
  const isLastMessageSentByMe = Boolean(
    currentMatrixUserId &&
    lastMessageSenderId &&
    String(lastMessageSenderId) === String(currentMatrixUserId)
  );

  const numericDaysLeftToReply =
    typeof daysLeftToReply === 'number' ? daysLeftToReply : null;

  const rawPreviewMessage = useMemo(
    () => lastMessage || last_message || message || '',
    [lastMessage, last_message, message]
  );

  const { message: previewMessage, isMediaPreview, mediaIconType } = useMemo(
    () => getPreviewData(rawPreviewMessage, isCreator, isLastMessageSentByMe),
    [rawPreviewMessage, isCreator, isLastMessageSentByMe]
  );

  const handleChatClick = useCallback(() => {
    if (!canOpenChat) return;

    navigate.toChat(userId, fromTab ? { state: { fromTab } } : {});
  }, [navigate, userId, fromTab, canOpenChat]);

  const handleExtendedClick = useCallback(
    async (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (!tipId) return;
      try {
        const result = await extendTipDeadline(tipId);
        if (result?.success) {
          dispatch(fetchMatrixRooms({ force: true }));
          if (!client || !roomId || !matrixEventId) return;

          try {
            await updateMatrixEventJsonBodyKey(client, {
              roomId,
              eventId: matrixEventId,
              key: 'isExtended',
              value: true,
            });
          } catch (error) {
            captureChatException(error, {
              tags: { feature: 'chat', action: 'extend-tip-update-matrix' },
            });
          }
        }
      } catch (error) {
        captureChatException(error, {
          tags: { feature: 'chat', action: 'extend-tip-deadline' },
        });
      }
    },
    [tipId, roomId, matrixEventId, client, dispatch]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!canOpenChat) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleChatClick();
      }
    },
    [handleChatClick, canOpenChat]
  );

  // tipLine =  '+$5.02 tipped'
  // previewMessage = 'Hello'
  // const fullPreview = tipLine ? `${tipLine} ${previewMessage}` : previewMessage;
  const messagePreview = truncate(previewMessage, PREVIEW_CHAR_LIMIT);

  const showPendingTipsBadge =
    isFirstMessageTip &&
    !isCreatorReplyToTip &&
    !isPriority &&
    daysLeftToReply > 0 &&
    pendingTips > 0;

  const shouldRefreshPendingTips =
    showPendingTipsBadge &&
    ((!isCreator && !isLastMessageSentByMe) || (isCreator && isLastMessageSentByMe));

  // refreshRooms is a function that refreshes the room list.
  // Avoid repeating the same refresh on every re-render. 
  const lastPendingTipsRefreshKeyRef = useRef(null);
  useEffect(() => {
    if (!roomId) return;
    if (!shouldRefreshPendingTips) return;

    const refreshKey = [
      roomId,
      String(pendingTips ?? ''),
      typeof daysLeftToReply === 'number' ? daysLeftToReply : String(daysLeftToReply ?? ''),
      String(isCreator),
      String(isLastMessageSentByMe),
    ].join('|');

    if (lastPendingTipsRefreshKeyRef.current === refreshKey) return;
    lastPendingTipsRefreshKeyRef.current = refreshKey;

    // 1) Refresh timeline-derived room state (useMatrixRooms)
    if (typeof refreshRooms === 'function') refreshRooms();
    // 2) Refresh room metadata powering the pending tips badge
    dispatch(fetchMatrixRooms({ force: true }));
  }, [
    roomId,
    shouldRefreshPendingTips,
    pendingTips,
    daysLeftToReply,
    isCreator,
    isLastMessageSentByMe,
    refreshRooms,
    dispatch,
  ]);

  const isWhale = Number(total) >= WHALE_THRESHOLD && isCreator;

  const canExtendDeadline =
    !isCreator &&
    !isExtended &&
    typeof numericDaysLeftToReply === 'number' &&
    numericDaysLeftToReply <= MESSAGE_DEADLINE_DAYS_EXTENDED_NOTIFY;

  const shouldShowCreatorDeadlineNotice =
    !isCreator &&
    typeof numericDaysLeftToReply === 'number' &&
    numericDaysLeftToReply <= MESSAGE_REPLY_DEADLINE_DAYS_NOTIFY;

  const mediaIcon =
    isMediaPreview && mediaIconType ? MEDIA_ICON_MAP[mediaIconType] : null;

  const isArchivedConversation = Boolean(
    item?.isArchived || item?.archived || item?.is_archived || item?.subscribed === false
  );

  if (!roomId) return null;

  return (
    <div
      className={`msg_user_card${canOpenChat ? '' : ' msg_user_card_chat_pending'}`}
      translate="no"
      role={canOpenChat ? 'button' : 'group'}
      tabIndex={canOpenChat ? 0 : -1}
      aria-busy={canOpenChat ? undefined : true}
      aria-disabled={canOpenChat ? undefined : true}
      aria-label={
        canOpenChat
          ? `Open chat with ${displayName}`
          : `Loading chat link for ${displayName}`
      }
      onClick={handleChatClick}
      onKeyDown={handleKeyDown}
    >
      <div className="msg_user_card_avatar">
        <LazyImage
          className={getRingClass(roomId)}
          src={avatarSrc}
          alt={`${displayName} avatar`}
          placeholder={DEFAULT_AVATAR}
        />

        {msg_count > 0 && (
          <span className="user_msg_count_chip_sm">{msg_count}</span>
        )}
      </div>

      <div className="msg_user_card_content">
        <div className="d-flex w-full align-items-center">

          <div className="username-section d-flex align-items-center gap-2 flex-wrap">
            <h6>
              @{displayName}

              {isArchivedConversation && (
                <span className="msg_card_locked_badge" aria-label="Locked chat">
                  <i className="fa-solid fa-lock" aria-hidden="true" />
                </span>
              )}

              {showPendingTipsBadge && (
                <span className="amount_pending" aria-label="Tip amount pending">
                  +${formatAmountWithTwoDecimalPlaces(pendingTips)} pending
                </span>
              )}

              {isPriority && (
                <span
                  className={`msg_card_priority_badge${isArchivedConversation ? ' msg_card_priority_badge_archived' : ''}`}
                >
                  <b
                    className={isArchivedConversation ? 'dot_gray_msg' : 'dot_green_msg'}
                    aria-hidden="true"
                  />
                  <span>Priority</span>
                </span>
              )}

              {isWhale && <span aria-label="Whale subscriber"> 🐋</span>}
            </h6>
          </div>

          {badge && <span className="amount_pending">{badge}</span>}

          {(isLastMessageSentByMe || time) && (
            <div className="ml-auto msg_card_meta_status">
              {isLastMessageSentByMe && (
                <span className="msg_card_sent_status" aria-label="Message sent status">
                  Sent
                </span>
              )}
              {time && <time dateTime={time}>{time}</time>}
            </div>
          )}

          {msg_count > 0 && (
            <span className="chat_user_active" aria-hidden="true" />
          )}
        </div>

        {messagePreview ? (
          <div
            title={messagePreview}
            className={
              isMediaPreview ? 'msg_card_preview d-flex align-items-center gap-2' : 'msg_card_preview'
            }
          >
            {mediaIcon ? (
              <i
                className={`fa-solid ${mediaIcon}`}
                aria-hidden
                style={{ flexShrink: 0 }}
              />
            ) : null}
            <span>{messagePreview}</span>
          </div>
        ) : null}

        {shouldShowCreatorDeadlineNotice && numericDaysLeftToReply !== null && (
          <div className="msg_user_card_footer d-flex align-items-center justify-content-between">
            <span
              className="msg_creator_deadline"
              style={{
                color:
                  numericDaysLeftToReply > MESSAGE_DEADLINE_DAYS_EXTENDED_NOTIFY ? 'rgb(234, 179, 8)' : '#f97373',
              }}
            >
              Creator has {numericDaysLeftToReply}{' '}
              {numericDaysLeftToReply === 1 ? 'day' : 'days'} to respond
            </span>

            {canExtendDeadline && (
              <button
                type="button"
                className="extend_deadline_btn"
                onClick={handleExtendedClick}
                aria-label="Extend reply deadline"
              >
                Extend by 5 days
              </button>
            )}

            {extended && (
              <span className="extended_days">{extended}</span>
            )}
          </div>
        )}

        {isCreator && daysRemaining && (
          <div className="d-flex align-items-center gap-2 justify-content-between">
            <span
              className={`cm_day_remaining${numericDaysLeftToReply !== null && numericDaysLeftToReply <= 2
                ? ' cm_day_remaining_danger'
                : ''
                }`}
            >
              {daysRemaining}
            </span>

            {extended && (
              <span className="extended_days">{extended}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(MessageCard);